import { ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import type {
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
} from 'react-hook-form';

import { Field, FieldDescription, FieldError } from '@/components/ui/field';

type FormFieldShellRenderProps<TFieldValues extends FieldValues> = {
  field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
  fieldState: ControllerFieldState;
  fieldId: string;
};

type FormFieldShellProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  description?: string;
  render: (props: FormFieldShellRenderProps<TFieldValues>) => ReactNode;
};

export const getFormFieldId = (name: string) => `form-field-${name}`;

export const FormFieldShell = <TFieldValues extends FieldValues>(
  props: FormFieldShellProps<TFieldValues>,
) => {
  const { name, description, render } = props;
  const { control } = useFormContext<TFieldValues>();
  const fieldId = getFormFieldId(String(name));

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          {render({ field, fieldState, fieldId })}
          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
          {description && <FieldDescription>{description}</FieldDescription>}
        </Field>
      )}
    />
  );
};
